import React, { useEffect, useState } from 'react';
import RouteBuilder from 'src/utils/routing/RouteBuilder';
import { useRouteBuilder } from 'src/utils/routing/RouteBuilderContext';
import { FunctionComponentReturnType } from 'src/types/sharedReact';
import {
  AuthenticationContextState,
  useAuthenticationContext
} from 'src/contextProviders/AuthenticationProvider/AuthenticationContext';
import "./QuicksightDashboardV2.css";
import axios from 'axios';
import { navigate, useLocation, useParams } from '@reach/router';
import { GetQuicksightBoardInfoResponse } from 'src/types/apiResponse';
import { createEmbeddingContext, EmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import NotAuthorizedPage from 'src/pages/NotAuthorizedPage/NotAuthorizedPage';

import '@cof/dxo-ui-button';

// ====== CONSTANTS ======
const QUICKSIGHT_URL_REGEX = /^https:\/\/us-[a-z]+-[1-9]\.quicksight\.aws\.amazon\.com\/embed/;
const DEFAULT_DASHBOARD_NAME = '...';
const DASHBOARD_TITLE_ELEMENT_ID = 'page-template-title-element';
const QUICKSIGHT_CONTAINER_ID = 'quicksight-container-v2';
const DASHBOARD_HEIGHT = '80%';

/**
 * QuicksightDashboardV2 - Embedded AWS QuickSight Dashboard Viewer
 *
 * Displays an embedded QuickSight dashboard with navigation back to analytics homepage.
 *
 * Key Features:
 * - Fetches QuickSight dashboard URL from API
 * - Validates URL format before embedding
 * - Embeds dashboard using AWS QuickSight SDK
 * - Updates page title with dashboard name
 * - Error handling for failed loads
 * - Return navigation to analytics homepage
 *
 * URL Parameters:
 * - dashboardId: Dashboard ID to load
 *
 * Effects (1 total):
 * 1. Dashboard loading effect - Fetches URL and embeds QuickSight dashboard
 */
function QuicksightDashboardV2(): FunctionComponentReturnType {
  // ====== CONTEXT & HOOKS ======
  const routeBuilder: RouteBuilder = useRouteBuilder();
  const { user }: AuthenticationContextState = useAuthenticationContext();
  const { dashboardId } = useParams();
  const location = useLocation();

  // ====== STATE ======
  const [didUrlRequestFail, setDidUrlRequestFail] = useState<boolean>(false);
  const [dashboardName, setDashboardName] = useState<string>(DEFAULT_DASHBOARD_NAME);

  // ====== API ROUTES ======
  const apiRouteGetQuicksightBoardInfo: string = routeBuilder.api.getQuicksightBoardInfo({ dashboardId });
  console.log('FLAG FOR QUICKSIGHT ROUTE', apiRouteGetQuicksightBoardInfo);
  console.log('FLAG FOR DASHBOARD ID IN V2', dashboardId)

  // ====== DASHBOARD LOADING ======
  /**
   * Effect: Load and embed QuickSight dashboard
   * - Fetches dashboard URL from API
   * - Validates URL format
   * - Creates embedding context
   * - Embeds dashboard into container
   * - Updates page title
   * - Handles errors
   */
  useEffect(() => {
    /**
     * Updates the page title element if it exists
     */
    const updatePageTitle = (title: string) => {
      const titleElement = document.getElementById(DASHBOARD_TITLE_ELEMENT_ID);
      if (titleElement) {
        titleElement.textContent = title;
      }
    };

    /**
     * Validates QuickSight URL format
     */
    const isValidQuicksightUrl = (url: string): boolean => {
      return QUICKSIGHT_URL_REGEX.test(url);
    };

    /**
     * Loads and embeds the QuickSight dashboard
     */
    async function loadDashboard() {
      // Set initial loading state
      updatePageTitle(DEFAULT_DASHBOARD_NAME);

      // Create embedding context
      const embeddingContext: EmbeddingContext = await createEmbeddingContext({
        onChange: (changeEvent) => {
          console.log('QuickSight context received a change', changeEvent);
        }
      });
      const { embedDashboard } = embeddingContext;

      try {
        // Fetch dashboard info from API
        console.log('dashboardId:', dashboardId, 'apiRoute:', apiRouteGetQuicksightBoardInfo);
        const response = await axios.get<GetQuicksightBoardInfoResponse>(apiRouteGetQuicksightBoardInfo);

        if (response.status === 200) {
          // dashboardUrl = anonUrl
          const dashboardUrl = response.data.dashboardUrl;

          // Validate URL format
          if (dashboardUrl && isValidQuicksightUrl(dashboardUrl)) {
            // Update page title and state
            const name = response.data.dashboardName || 'Dashboard';
            updatePageTitle(name);
            setDashboardName(name);

            // Embed dashboard
            const frameOptions = {
              url: dashboardUrl,
              height: DASHBOARD_HEIGHT,
              resizeHeightOnSizeChangedEvent: true,
              container: document.getElementById(QUICKSIGHT_CONTAINER_ID),
              withIframePlaceholder: true
            };
            await embedDashboard(frameOptions, {});
          } else {
            console.error('Invalid QuickSight URL format:', dashboardUrl);
            setDidUrlRequestFail(true);
          }
        }
      } catch (error) {
        console.error('Failed to load QuickSight dashboard:', error);
        setDidUrlRequestFail(true);
      }
    }

    loadDashboard();
  }, [apiRouteGetQuicksightBoardInfo]);

  // ====== EVENT HANDLERS ======
  /**
   * Navigates back to the home page for the current partner
   */
  const handleReturn = () => {
    console.log("'Return to Home' button clicked");
    navigate(routeBuilder.client.toHomePageV2());
  };

  // ====== RENDER ======
  return (
    <div className="quicksight-dashboard-v2">
      <div className="quicksight-section">
        {/* Header with dashboard title and return button */}
        <div className="quicksight-section-header">
          <div className="quicksight-header-content">
            <h3>{dashboardName}</h3>
            <div className="quicksight-return-button" onClick={handleReturn}>
              <dxo-ui-button
                style-variant="grv-button tertiary"
                id="copp-button-return-v2"
                name="return-to-analytics"
                text="← Return to Home"
                type="button"
              />
            </div>
          </div>
        </div>

        {/* Dashboard content area — swaps to a restricted-access notice when
            the dashboard can't load (no access, missing, or temporarily down). */}
        <div className="quicksight-content">
          {didUrlRequestFail ? (
            <NotAuthorizedPage
              heading="Unable to load this dashboard"
              description="This may be due to a temporary outage, a missing dashboard, or insufficient access permissions. If the issue persists, please contact support."
            />
          ) : (
            <div id={QUICKSIGHT_CONTAINER_ID} className="quicksight-container-v2"></div>
          )}
        </div>
      </div>
    </div>
  );
}

export default QuicksightDashboardV2;